/**
 * NetworkStatusBanner — [T68P1-c-3]
 * Fixed top banner that shows when the user is offline or when the sync queue
 * is being flushed after reconnection. Animates in/out via CSS transitions.
 */
import * as React from 'react';
import { WifiOff, RefreshCw, CheckCircle } from 'lucide-react';
import { useOfflineSync } from '@/contexts/OfflineSyncContext';
import { cn } from '@/lib/utils';

export function NetworkStatusBanner() {
  const {
    isOnline,
    isBrowserOnline,
    backendReachable,
    queueSize,
    isFlushing,
  } = useOfflineSync();
  const [showReconnected, setShowReconnected] = React.useState(false);
  const prevOnlineRef = React.useRef(isOnline);

  // Show a transient "Back online" banner for 3 seconds after reconnecting
  React.useEffect(() => {
    if (!prevOnlineRef.current && isOnline) {
      setShowReconnected(true);
      const timer = setTimeout(() => setShowReconnected(false), 3000);
      prevOnlineRef.current = true;
      return () => clearTimeout(timer);
    }
    prevOnlineRef.current = isOnline;
  }, [isOnline]);

  const isVisible = !isOnline || isFlushing || showReconnected;

  if (!isVisible) return null;

  if (!isOnline) {
    const statusMessage = !isBrowserOnline
      ? 'You\'re offline.'
      : backendReachable === false
        ? 'The server is unavailable. You\'re in offline mode.'
        : 'You\'re offline.';
    return (
      <div
        role="status"
        aria-live="polite"
        className={cn(
          'w-full flex items-center gap-2 px-4 py-2 text-sm font-medium',
          'bg-orange-500 text-white',
          'transition-transform duration-300'
        )}
      >
        <WifiOff className="h-4 w-4 shrink-0" />
        <span>
          {statusMessage}{' '}
          {queueSize > 0
            ? `${queueSize} change${queueSize > 1 ? 's' : ''} will sync when you reconnect.`
            : 'Changes will sync when you reconnect.'}
        </span>
      </div>
    );
  }

  if (isFlushing) {
    return (
      <div
        role="status"
        aria-live="polite"
        className={cn(
          'w-full flex items-center gap-2 px-4 py-2 text-sm font-medium',
          'bg-blue-600 text-white',
          'transition-transform duration-300'
        )}
      >
        <RefreshCw className="h-4 w-4 shrink-0 animate-spin" />
        <span>Syncing {queueSize > 0 ? `${queueSize} pending change${queueSize > 1 ? 's' : ''}` : 'changes'}…</span>
      </div>
    );
  }

  // showReconnected — transient green banner
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'w-full flex items-center gap-2 px-4 py-2 text-sm font-medium',
        'bg-emerald-600 text-white',
        'transition-transform duration-300'
      )}
    >
      <CheckCircle className="h-4 w-4 shrink-0" />
      <span>Back online — all changes synced.</span>
    </div>
  );
}
